import { memo, useState, useMemo, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { getStyleImageUrl, type StyleSummary } from '../../../../api/styles'
import { STYLE_FILTER_TABS, DISABLED_FILTER_TABS } from '../../../../constants/styles'
import { useStyleLibrary } from './useStyleLibrary'
import { StyleLightbox } from './StyleLightbox'

export interface StyleLibraryModalProps {
  isOpen: boolean
  onClose: () => void
  currentStyleId: string
  /** Canvas mode: X-close applies selection via onApply(selectedStyleId). */
  onApply?: (styleId: string) => void
  /** Landing mode: clicking a card immediately calls onSelect(styleId). */
  onSelect?: (styleId: string) => void
}

/**
 * Full-screen centered Style Library Modal shared by canvas nodes and
 * the landing page.
 *
 * Canvas mode — all dismiss paths (X button, Escape, backdrop click)
 * save the current selection via onApply(selectedStyleId) when the user
 * has clicked a style card. If no card was clicked, they simply close
 * via onClose().
 *
 * Landing mode:
 * - Clicking a card immediately calls onSelect(styleId)
 * - X button / Escape / backdrop click all call onClose()
 */
const StyleLibraryModal = memo(({
  isOpen,
  onClose,
  onApply,
  onSelect,
  currentStyleId,
}: StyleLibraryModalProps) => {
  const { styles, isLoading } = useStyleLibrary(isOpen)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [activeTag, setActiveTag] = useState<string>('All')
  const [hasUserSelected, setHasUserSelected] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)

  // Sync selectedId when modal opens
  useEffect(() => {
    if (isOpen) {
      // Check if currentStyleId exists in library
      const exists = styles.some((s) => s.id === currentStyleId)
      setSelectedId(exists ? currentStyleId : null)
      setHasUserSelected(false)
      setActiveTag('All')
      setLightboxUrl(null)
    }
  }, [isOpen, styles, currentStyleId])

  // Shared dismiss handler — applies the user's selection (if any) then closes.
  // Used by X button, backdrop click, and Escape key.
  const handleDismiss = useCallback(() => {
    if (onApply && hasUserSelected && selectedId) {
      onApply(selectedId)
    } else {
      onClose()
    }
  }, [hasUserSelected, selectedId, onApply, onClose])

  // Escape key handler — skips when lightbox is open (lightbox handles its own Escape)
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // If lightbox is open, it will handle Escape via its own capture-phase listener
        if (lightboxUrl) return
        e.stopPropagation()
        handleDismiss()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleDismiss, lightboxUrl])

  const handleCardClick = useCallback((styleId: string) => {
    setSelectedId(styleId)
    setHasUserSelected(true)
    // In landing mode, immediately notify the parent
    onSelect?.(styleId)
  }, [onSelect])

  // Filter styles by active tag
  const filteredStyles = useMemo(() => {
    if (activeTag === 'All') return styles
    return styles.filter((s) => s.tags.includes(activeTag))
  }, [styles, activeTag])

  // Find selected style for preview
  const selectedStyle = useMemo(
    () => styles.find((s) => s.id === selectedId),
    [styles, selectedId],
  )

  if (!isOpen) return null

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      data-testid="style-library-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={handleDismiss}
        data-testid="style-modal-backdrop"
      />

      {/* Modal content */}
      <div
        className="relative z-10 bg-background border border-white/10 rounded-xl shadow-2xl w-[90vw] max-w-[1000px] max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0">
          <h2 className="text-lg font-semibold text-foreground">Style Library</h2>
          <button
            onClick={handleDismiss}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground/50 hover:text-foreground hover:bg-white/10 transition-colors"
            data-testid="style-modal-close-btn"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Tag filter bar */}
        <div className="px-6 pt-4 flex-shrink-0">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin" data-testid="tag-filter-bar">
            {STYLE_FILTER_TABS.map((tag) => {
              const isActive = activeTag === tag
              const isDisabled = DISABLED_FILTER_TABS.has(tag)
              return (
                <button
                  key={tag}
                  onClick={() => !isDisabled && setActiveTag(tag)}
                  disabled={isDisabled}
                  data-testid={`tag-pill-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`
                    flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200
                    ${isActive
                      ? 'bg-pink-500/20 text-pink-400 border border-pink-500/40'
                      : isDisabled
                        ? 'bg-surface-1 text-foreground/20 border border-white/5 cursor-not-allowed'
                        : 'bg-surface-1 text-foreground/50 border border-white/10 hover:bg-surface-2 hover:text-foreground/70 cursor-pointer'
                    }
                  `}
                >
                  {tag}
                </button>
              )
            })}
          </div>
        </div>

        {/* Style grid */}
        <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0" data-testid="style-grid">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <svg className="animate-spin w-6 h-6 text-foreground/30" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          ) : filteredStyles.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-foreground/30 text-sm">
              No styles found for this tag
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {filteredStyles.map((style) => (
                <StyleCardInline
                  key={style.id}
                  style={style}
                  isSelected={selectedId === style.id}
                  onSelect={handleCardClick}
                />
              ))}
            </div>
          )}
        </div>

        {/* Selected style preview panel */}
        {selectedStyle && (
          <div className="px-6 pb-4 pt-2 border-t border-white/10 flex-shrink-0" data-testid="style-preview-panel">
            <h3 className="text-sm font-medium text-foreground/70 mb-3">
              Style Template Preview
            </h3>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {selectedStyle.thumbnail_url ? (
                <img
                  src={getStyleImageUrl(selectedStyle.thumbnail_url)}
                  alt={selectedStyle.name}
                  className="w-28 h-28 rounded-lg object-cover flex-shrink-0 border border-white/10 cursor-pointer hover:ring-2 hover:ring-white/30 transition-all"
                  onClick={() => setLightboxUrl(getStyleImageUrl(selectedStyle.thumbnail_url!))}
                  data-testid="style-preview-thumbnail"
                />
              ) : (
                <div className="w-28 h-28 rounded-lg bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center flex-shrink-0 border border-white/10">
                  <span className="text-foreground/20 text-xl font-bold">{selectedStyle.name[0]}</span>
                </div>
              )}
              {selectedStyle.preview_image_urls.map((url, i) => (
                <img
                  key={i}
                  src={getStyleImageUrl(url)}
                  alt={`${selectedStyle.name} preview ${i + 1}`}
                  className="w-28 h-28 rounded-lg object-cover flex-shrink-0 border border-white/10 cursor-pointer hover:ring-2 hover:ring-white/30 transition-all"
                  loading="lazy"
                  onClick={() => setLightboxUrl(getStyleImageUrl(url))}
                  data-testid={`style-preview-image-${i}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Lightbox overlay for full-size preview */}
        <StyleLightbox
          imageUrl={lightboxUrl}
          onClose={() => setLightboxUrl(null)}
        />
      </div>
    </div>
  )

  return createPortal(modal, document.body)
})

StyleLibraryModal.displayName = 'StyleLibraryModal'
export { StyleLibraryModal }

// -- Inline StyleCard (avoid importing from landing) --

interface StyleCardInlineProps {
  style: StyleSummary
  isSelected: boolean
  onSelect: (styleId: string) => void
}

const StyleCardInline = memo(({ style, isSelected, onSelect }: StyleCardInlineProps) => (
  <button
    onClick={() => onSelect(style.id)}
    data-testid={`style-card-${style.id}`}
    className={`
      group relative flex flex-col rounded-md cursor-pointer
      transition-all duration-200 border-2 bg-panel
      ${isSelected
        ? 'border-pink-500 ring-2 ring-pink-500/30'
        : 'border-transparent hover:border-pink-500/60'
      }
    `}
  >
    {/* Thumbnail */}
    <div className="relative aspect-square w-full overflow-hidden">
      {style.thumbnail_url ? (
        <img
          src={getStyleImageUrl(style.thumbnail_url)}
          alt={style.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center">
          <span className="text-foreground/20 text-2xl font-bold">{style.name[0]}</span>
        </div>
      )}

      {/* Hover preview fan */}
      {style.preview_image_urls.length > 0 && (
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
          {style.preview_image_urls.map((url, i) => (
            <img
              key={i}
              src={getStyleImageUrl(url)}
              alt={`${style.name} preview ${i + 1}`}
              className="absolute bottom-0 right-0 w-10 h-10 rounded-md border border-border object-cover shadow"
              style={{
                transform: `rotate(${(i - 1) * 8}deg) translate(${(i - 1) * -4}px, ${(i - 1) * -2}px)`,
                zIndex: i,
              }}
              loading="lazy"
            />
          ))}
        </div>
      )}

      {/* Selected badge */}
      {isSelected && (
        <div className="absolute top-1/2 -right-0.5 -translate-y-1/2 w-5 h-5 bg-pink-500 rounded-full flex items-center justify-center shadow z-10">
          <svg className="w-3 h-3 text-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 12 10 16 18 8" />
          </svg>
        </div>
      )}
    </div>

    {/* Name bar */}
    <div className="px-2 py-2 bg-background">
      <span className="text-xs text-foreground/70 font-medium truncate block">{style.name}</span>
    </div>
  </button>
))

StyleCardInline.displayName = 'StyleCardInline'
